import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGames, useUsers } from '@/hooks/useShared';
import { deleteGame } from '@/hooks/useGameSetup';
import { useAuth } from '@/hooks/useAuth';
import { LoadingSpinner, EmptyState, Modal, Card } from '@/components/ui';
import { toast } from 'sonner';
import { formatDate, formatCurrency } from '@/lib/utils';
import { canManageUsers } from '@/lib/roles';
import type { Game } from '@/lib/types';

export function LibraryPage() {
  const navigate = useNavigate();
  const { games, loading: gamesLoading } = useGames();
  const { loading: usersLoading } = useUsers();
  const { user } = useAuth();

  const isAdmin = canManageUsers(user);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const handleDelete = async (e: React.MouseEvent, gameId: string) => {
    e.stopPropagation();
    setConfirmDeleteId(gameId);
  };

  const confirmDelete = async () => {
    if (!confirmDeleteId) return;
    try {
      await deleteGame(confirmDeleteId, user?.id || '');
      toast.success('Game deleted successfully');
      setConfirmDeleteId(null);
    } catch (err) {
      toast.error('Failed to delete game');
    }
  };

  const allPastGames = useMemo(() => 
    games
      .filter(g => ['chip_entry', 'settling', 'settled', 'library'].includes(g.status))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [games]
  );

  const settlingGames = useMemo(() => allPastGames.filter(g => g.status === 'settling'), [allPastGames]);
  const interactiveGames = useMemo(() => allPastGames.filter(g => g.status === 'chip_entry'), [allPastGames]);
  const historyGames = useMemo(() => allPastGames.filter(g => ['settled', 'library'].includes(g.status)), [allPastGames]);

  if (gamesLoading || usersLoading) return <LoadingSpinner message="Loading Library..." />;

  const handleGameClick = (game: Game) => {
    navigate(`/settlement/${game.id}`);
  };

  const GameCard = ({ game, badge }: { game: Game; badge?: string }) => (
    <div
      key={game.id}
      className="relative bg-white rounded-[1rem] border border-gray-200 p-4 cursor-pointer transition-all duration-200 hover:shadow-md mb-3"
      onClick={() => handleGameClick(game)}
    >
      {isAdmin && (
        <button
          onClick={(e) => handleDelete(e, game.id)}
          className="absolute top-3 right-3 text-red-500 hover:text-red-700 transition-colors p-1"
          title="Delete Game"
          aria-label="Delete game"
        >
          🗑️
        </button>
      )}
      <div className="text-xs font-medium text-[#006855]">{formatDate(game.date)}</div>
      <div className="text-base font-bold text-[#2c2f30] mt-0.5">{game.venue || 'Friday Night Poker'}</div>
      <div className="flex gap-3 mt-2 text-xs text-[#6b7280] font-medium">
        <span>👥 {game.players.length} Players</span>
        <span>💰 {formatCurrency(game.settlement?.player_snapshot.reduce((sum, p) => sum + p.buyIn, 0) || 0)} Pot</span>
        {badge && <span className="text-amber-600 font-bold">{badge}</span>}
      </div>
    </div>
  );

  return (
    <>
      <div className="fixed top-2 right-2 z-[10000] px-2 py-1 rounded text-[10px] font-bold uppercase pointer-events-none"
        style={{ backgroundColor: isAdmin ? '#22c55e' : '#ef4444', color: 'white' }}>
        Admin: {isAdmin ? 'ON' : 'OFF'}
      </div>
      <div className="max-w-md mx-auto py-6 px-4 space-y-6 min-h-screen bg-[#f5f7f8]">
        <div className="bg-[#e8f5f0] border border-[#006855]/10 rounded-2xl p-5 mb-4">
          <div className="text-[10px] font-black uppercase tracking-[2px] text-[#006855]/70 mb-1">GAME ARCHIVE</div>
          <h1 className="text-[22px] font-extrabold text-[#2c2f30] leading-tight">Library</h1>
        </div>

        {allPastGames.length === 0 ? (
          <Card className="border border-gray-200">
            <EmptyState icon="📁" title="No library games" description="Completed and library games will appear here." />
          </Card>
        ) : (
          <>
            {settlingGames.length > 0 && (
              <div>
                <h2 className="text-[10px] font-black text-amber-600 uppercase tracking-[2px] mb-3">Pending Settlement</h2>
                {settlingGames.map(game => (
                  <GameCard key={game.id} game={game} badge="Settle Now →" />
                ))}
              </div>
            )}
            {interactiveGames.length > 0 && (
              <div>
                <h2 className="text-[10px] font-black text-[#6b7280] uppercase tracking-[2px] mb-3">Open Settlements</h2>
                {interactiveGames.map(game => (
                  <GameCard key={game.id} game={game} />
                ))}
              </div>
            )}
            {historyGames.length > 0 && (
              <div>
                <h2 className="text-[10px] font-black text-[#6b7280] uppercase tracking-[2px] mb-3">Game History</h2>
                {historyGames.map(game => (
                  <GameCard key={game.id} game={game} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
      <Modal
        isOpen={!!confirmDeleteId}
        onClose={() => setConfirmDeleteId(null)}
        title="Delete Game"
      >
        <div className="text-[#6b7280] mb-6">
          Are you sure you want to delete this game? This action cannot be undone.
        </div>
        <div className="flex justify-end gap-3">
          <button
            onClick={() => setConfirmDeleteId(null)}
            className="px-4 py-2 rounded-xl font-bold bg-white border border-gray-200 text-[#2c2f30] hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={confirmDelete}
            className="px-4 py-2 rounded-xl font-bold bg-red-500 text-white hover:bg-red-600 transition-colors"
          >
            Delete
          </button>
        </div>
      </Modal>
    </>
  );
}
